import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { ConflictNotice } from '../ui/ConflictNotice';
import { REJECT_REASON_LABELS, type RejectReason, type TransitionResult, type Version } from './types';
import { DialogLayer } from '../ui/Dialog';
import { NotesInput } from '../ui/NotesInput';

/**
 * The client's answer to a sent version: "Zapisz akceptację" or "Odrzucona", with a note. Only one version can be
 * accepted: accepting another asks first, and the one accepted before goes back to "wysłana".
 */
export function DecisionDialog({ version, mode, onDone, onClose, onReload }: { version: Version; mode: 'accept' | 'reject'; onDone: (v: TransitionResult) => void; onClose: () => void; onReload: () => void }) {
  const [note, setNote] = useState('');
  // P8 (owner note 00:21): a rejection says why; no "are you sure"
  const [reason, setReason] = useState<RejectReason | null>(null);
  const [missing, setMissing] = useState(false);
  const [replace, setReplace] = useState<number | null>(null);
  const decide = useMutation({
    mutationFn: (replaceAccepted: boolean) =>
      api<TransitionResult>(`/api/quotes/versions/${version.id}/${mode}`, { method: 'POST', body: { note, version: version.version, ...(mode === 'reject' && reason ? { reason } : {}), ...(replaceAccepted ? { replaceAccepted: true } : {}) } }),
    onSuccess: onDone,
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'accepted_exists') setReplace((e.current as { number?: number } | undefined)?.number ?? 0);
    },
  });
  function submit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'reject' && !reason) return setMissing(true);
    decide.mutate(replace !== null);
  }
  const error = decide.error instanceof ApiError ? decide.error : null;
  const accept = mode === 'accept';
  const title = accept ? `Akceptacja v${version.number}` : `Odrzucenie v${version.number}`;
  return (
    <div className="dialog-backdrop">
      <form className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="decision-title" onSubmit={submit} data-feature-id="quotes.accept">
        <DialogLayer onClose={onClose} busy={decide.isPending} />
        <h2 id="decision-title">{title}</h2>
        <p className="muted">{accept ? 'Klient zaakceptował tę wersję. Wydarzenie zostanie potwierdzone, jeśli planowanie jest pełne.' : 'Klient odrzucił tę wersję. Potem możesz przygotować nową.'}</p>
        {error?.status === 409 && error.code !== 'accepted_exists' && <ConflictNotice onReload={onReload} />}
        {!accept && (
          <fieldset className="q-reasons">
            <legend>Powód</legend>
            {(Object.keys(REJECT_REASON_LABELS) as RejectReason[]).map((r) => (
              <label key={r} className="check">
                <input
                  type="radio"
                  name="reason"
                  value={r}
                  checked={reason === r}
                  onChange={() => {
                    setReason(r);
                    setMissing(false);
                  }}
                />
                <span>{REJECT_REASON_LABELS[r]}</span>
              </label>
            ))}
            {missing && (
              <p className="field-error" role="alert">
                Wybierz powód odrzucenia
              </p>
            )}
          </fieldset>
        )}
        <label className="field">
          <span>Notatka (nieobowiązkowo)</span>
          <NotesInput value={note} onChange={(e) => setNote(e.target.value)} autoFocus={accept} placeholder={accept ? 'np. mailem 20.10' : 'np. wybrali tańszą ofertę'} />
        </label>
        {replace !== null && (
          <p className="alert" role="alert">
            Zaakceptowana jest już wersja v{replace}. Jeśli klient wybrał jednak v{version.number}, zatwierdź jeszcze raz: v{replace} wróci do stanu „wysłana”.
          </p>
        )}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {fieldIssues(error)[0]?.message ?? error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={decide.isPending}>
            {accept ? (replace !== null ? `Zaakceptuj v${version.number} zamiast v${replace}` : 'Zapisz akceptację') : 'Zapisz odrzucenie'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
